<template>
  <div>
    <v-navigation-drawer
      v-if="!isCompact"
      app
      width="276"
      mini-variant-width="84"
      :mini-variant="sidebarMini"
      :class="['app-drawer', { 'app-drawer--mini': sidebarMini }]"
      color="#0d1f1c"
      permanent
    >
      <div class="drawer-brand-row">
        <BrandLogo v-if="!sidebarMini" dark />
        <BrandLogo v-else class="mini-brand-logo" dark mark-only />
        <v-btn icon small class="drawer-collapse-button" :aria-label="sidebarMini ? 'Expandir menu' : 'Recolher menu'" @click="toggleSidebarMini">
          <v-icon small>{{ sidebarMini ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
        </v-btn>
      </div>

      <div class="app-nav">
        <div v-for="section in navSections" :key="section.label" class="app-nav-section">
          <span class="app-nav-label">{{ section.label }}</span>
          <v-list dense nav>
            <v-list-item v-for="item in section.items" :key="item.to" :to="item.to" link>
              <v-list-item-icon>
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ item.label }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.caption">{{ item.caption }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </div>

      <router-link class="drawer-usage-footer" to="/app/billing" :title="sidebarMini ? `${planName} · ${quotaUsed}/${quotaLimit} créditos` : null">
        <v-icon v-if="sidebarMini" class="drawer-usage-mini-icon">mdi-chart-donut</v-icon>
        <div class="drawer-usage-row">
          <span>Plano atual</span>
          <strong>{{ planName }}</strong>
        </div>
        <div class="drawer-usage-row">
          <span>Créditos</span>
          <strong>{{ quotaUsed }} / {{ quotaLimit }}</strong>
        </div>
        <v-progress-linear :value="quotaPercent" height="4" rounded color="primary" background-color="rgba(255,255,255,.14)" />
      </router-link>
    </v-navigation-drawer>

    <v-navigation-drawer
      v-else
      v-model="drawer"
      app
      width="276"
      class="app-drawer"
      color="#0d1f1c"
      temporary
      fixed
    >
      <div class="drawer-brand-row drawer-brand-row--temporary">
        <BrandLogo dark />
      </div>

      <div class="app-nav">
        <div v-for="section in navSections" :key="section.label" class="app-nav-section">
          <span class="app-nav-label">{{ section.label }}</span>
          <v-list dense nav>
            <v-list-item v-for="item in section.items" :key="item.to" :to="item.to" link @click="closeTemporaryDrawer">
              <v-list-item-icon>
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ item.label }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.caption">{{ item.caption }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </div>

      <router-link class="drawer-usage-footer" to="/app/billing" @click.native="closeTemporaryDrawer">
        <div class="drawer-usage-row">
          <span>Plano atual</span>
          <strong>{{ planName }}</strong>
        </div>
        <div class="drawer-usage-row">
          <span>Créditos</span>
          <strong>{{ quotaUsed }} / {{ quotaLimit }}</strong>
        </div>
        <v-progress-linear :value="quotaPercent" height="4" rounded color="primary" background-color="rgba(255,255,255,.14)" />
      </router-link>
    </v-navigation-drawer>

    <v-app-bar app flat class="app-topbar" height="72">
      <v-app-bar-nav-icon
        v-if="isCompact"
        class="menu-toggle"
        aria-label="Abrir menu"
        @click.stop="drawer = true"
      />
      <div>
        <strong>{{ tenantName }}</strong>
        <div class="app-bar-caption">Validação documental e compliance operacional</div>
      </div>
      <v-spacer />
      <v-btn text color="primary" class="logout-button" @click="logout">Sair</v-btn>
    </v-app-bar>

    <v-main class="app-main">
      <router-view />
      <footer class="app-footer">
        <p>© {{ currentYear }} ComplyOps. Todos os direitos reservados.</p>
        <div class="footer-powered">
          <span>Powered by</span>
          <img :src="mazzitechLogo" alt="Mazzitech">
        </div>
      </footer>
    </v-main>
  </div>
</template>

<script>
import BrandLogo from '../components/BrandLogo.vue'
import mazzitechLogo from '../assets/images/logo-mazzitech.png'
import api from '../plugins/axios'

export default {
  name: 'AppLayout',
  components: {
    BrandLogo
  },
  data () {
    return {
      drawer: false,
      sidebarMini: false,
      menu: [
        { label: 'Visão geral', caption: 'Painel central', icon: 'mdi-view-dashboard-outline', to: '/app/dashboard', group: 'Principal' },
        { label: 'Gerar documento', caption: 'Documento assistido', icon: 'mdi-file-document-edit-outline', to: '/app/gerar-documento', group: 'Documentos' },
        { label: 'Documentos', caption: 'Drive interno', icon: 'mdi-folder-text-outline', to: '/app/documentos', group: 'Documentos' },
        { label: 'Validar documento', caption: 'Controle de saída', icon: 'mdi-file-search-outline', to: '/app/validar-documento', group: 'Documentos' },
        { label: 'Metadados CNJ', caption: 'Consulta DataJud', icon: 'mdi-database-search-outline', to: '/app/metadados-judiciais', group: 'Documentos' },
        { label: 'Revisões humanas', caption: 'Fila de decisão', icon: 'mdi-account-check-outline', to: '/app/solicitacoes', group: 'Documentos' },
        { label: 'Políticas', caption: 'Regras de uso', icon: 'mdi-shield-check-outline', to: '/app/politicas', group: 'Controle' },
        { label: 'Evidências', caption: 'Histórico gerado', icon: 'mdi-file-document-check-outline', to: '/app/evidencias', group: 'Controle' },
        { label: 'Equipe', caption: 'Acessos internos', icon: 'mdi-account-group-outline', to: '/app/funcionarios', group: 'Controle' },
        { label: 'Auditoria', caption: 'Trilha operacional', icon: 'mdi-clipboard-text-clock-outline', to: '/app/audit', group: 'Controle' },
        { label: 'Minha conta', caption: 'Perfil profissional', icon: 'mdi-account-circle-outline', to: '/app/minha-conta', group: 'Conta' },
        { label: 'Conta', caption: 'Plano e cobrança', icon: 'mdi-credit-card-outline', to: '/app/billing', group: 'Conta' }
      ],
      subscription: {},
      quota: {},
      mazzitechLogo
    }
  },
  computed: {
    currentYear () {
      return new Date().getFullYear()
    },
    isCompact () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    tenantName () {
      return this.$store.state.auth.tenant ? this.$store.state.auth.tenant.name : 'ComplyOps'
    },
    planName () {
      return this.subscription && this.subscription.plan ? this.subscription.plan.name : 'Trial'
    },
    quotaUsed () {
      return Number(this.quota.tenant_used || 0)
    },
    quotaLimit () {
      return Number(this.quota.tenant_limit || 0)
    },
    quotaPercent () {
      if (!this.quotaLimit) return 0
      return Math.min(Math.round((this.quotaUsed / this.quotaLimit) * 100), 100)
    },
    navSections () {
      const groups = ['Principal', 'Documentos', 'Controle', 'Conta']
      return groups
        .map(label => ({
          label,
          items: this.menu.filter(item => item.group === label)
        }))
        .filter(section => section.items.length)
    }
  },
  created () {
    this.sidebarMini = localStorage.getItem('complyops:sidebar-mini') === 'true'
    this.loadAccountSummary()
  },
  methods: {
    toggleSidebarMini () {
      this.sidebarMini = !this.sidebarMini
      localStorage.setItem('complyops:sidebar-mini', this.sidebarMini ? 'true' : 'false')
    },
    async loadAccountSummary () {
      try {
        const { data } = await api.get('/billing/subscription')
        this.subscription = data.subscription || {}
        this.quota = data.ai_validation_quota || {}
      } catch (error) {
        this.subscription = {}
        this.quota = {}
      }
    },
    closeTemporaryDrawer () {
      if (this.$vuetify.breakpoint.mdAndDown) {
        this.drawer = false
      }
    },
    async logout () {
      await this.$store.dispatch('auth/logout')
      this.$router.push({ name: 'login' })
    }
  }
}
</script>
